import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { shouldRefetchOnRefocus } from './refocusThrottle';
import { readScreenCache, fetchScreenData } from '../lib/screenDataCache';

// PAGER-1 (remate del censo) — LA LLAVE PROPIA DEL AVISO DE DOWNGRADE.
//
// EL HALLAZGO: `GET /payments/downgrade-notice` salía DOS veces con el Panel
// abierto —el banner (hijo del Panel) y el Sidebar (chasis) montan el mismo
// fetch— y ninguna de las dos pasaba por cache ni por dedupe. Era la única
// lectura de pantalla del repo fuera de `screenDataCache`.
//
// Enrutada por la llave `downgrade-notice` gana las dos propiedades del módulo,
// y NADA MÁS: dedupe de concurrentes (los dos consumidores montando a la vez =
// UNA petición) y la ventana de frescura de 30 s (un consumidor que monta
// dentro de esa ventana pinta la entrada y no vuelve a preguntar). Esto sólo
// REDUCE llamadas, que es exactamente lo que la ley del módulo permite
// (`screenDataCache.js`: "jamás agrega una").
//
// LO QUE NO CAMBIA, A PROPÓSITO: los DISPARADORES de refresco siguen siendo los
// de hoy —foco/`visibilitychange` con su throttle, y `docs-changed`— y la llave
// NO se añade a la lista de invalidación de NET.UP. Un `refresh()` explícito
// jamás consulta la ventana de frescura: pide siempre (y se dedupea si otro
// consumidor pidió en el mismo instante), igual que antes.
export const DOWNGRADE_NOTICE_KEY = 'downgrade-notice';

const NO_NOTICE = { show: false };
const normalize = (data) => (data && typeof data.show === 'boolean' ? data : NO_NOTICE);

/**
 * Shared read of the scheduled-downgrade notice (GET /payments/downgrade-notice — cache-backed,
 * never calls Stripe). Same fail-safe + refetch-on-focus/docs-changed contract the banner uses,
 * exposed as a hook so the dashboard "choose documents" link, the banner and Settings can gate on
 * the SAME signal (scheduled downgrade AND over the new tope) WITHOUT the banner's per-day
 * dismiss. Never throws; any error / miss -> {show:false}.
 *
 * PAGER-1 — `activo` (default TRUE, o sea: nadie cambia si no lo pasa). Con
 * `activo:false` esta instancia no fetchea al montar y no escucha foco ni
 * `docs-changed`. Lo usa el `DowngradeBanner`, que es HIJO del Panel y sigue la
 * activación de su raíz; el Sidebar vive en el CHASIS —siempre visible, nunca
 * dormido— y por eso no pasa nada y no gatea.
 */
export function useDowngradeNotice({ activo = true } = {}) {
  // Semilla del cache UNA vez al montar (regla de frescura F4.3b, la misma que
  // usan las demás pantallas): fresca → se pinta y no se pregunta.
  const [seed] = useState(() => readScreenCache(DOWNGRADE_NOTICE_KEY));
  const seedFresh = Boolean(seed?.fresh);
  const [notice, setNotice] = useState(seedFresh ? normalize(seed.data) : NO_NOTICE);
  // La semilla fresca cuenta como fetch para el throttle de refoco: no se
  // re-pide de inmediato lo que se acaba de pintar.
  const lastFetchRef = useRef(seedFresh ? Date.now() : 0);

  const refresh = useCallback(async () => {
    lastFetchRef.current = Date.now();
    try {
      const data = await fetchScreenData(DOWNGRADE_NOTICE_KEY, async () => {
        const res = await api.get('/payments/downgrade-notice');
        return normalize(res.data);
      });
      setNotice(normalize(data));
    } catch {
      setNotice(NO_NOTICE); // fail-safe
    }
  }, []);

  useEffect(() => {
    if (!activo) return undefined;
    if (!seedFresh) refresh();
    const onRefocus = () => {
      if (shouldRefetchOnRefocus(Date.now(), lastFetchRef.current, document.visibilityState)) refresh();
    };
    const onChanged = () => refresh();
    window.addEventListener('focus', onRefocus);
    document.addEventListener('visibilitychange', onRefocus);
    window.addEventListener('studybuddy:docs-changed', onChanged);
    return () => {
      window.removeEventListener('focus', onRefocus);
      document.removeEventListener('visibilitychange', onRefocus);
      window.removeEventListener('studybuddy:docs-changed', onChanged);
    };
  }, [refresh, seedFresh, activo]);

  return { notice, refresh };
}

export default useDowngradeNotice;
